import { Component, Input, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Documents } from '../../models/plant-manager.model';
import { Attachments } from '../../../project-manager/models/attachments.model';
import { GlobalEmittersService } from 'src/app/services/global-emitters.service';
import { LinkEntityType } from '../../../project-manager/enums/LinkEntityType.enum';
import { UserUtilityService } from 'src/app/services';
import { CustomValidatorService } from 'src/app/services/custom-validator.service';
import { CustomToastService } from 'src/app/services/custom-toast.service';

@Component({
    selector: 'app-document-upload',
    templateUrl: './document-upload.component.html',
    styleUrls: ['./document-upload.component.scss'],
    standalone: false
})
export class DocumentUploadComponent implements OnInit {
    @Input() headingName: string;
    @Input() modalMode: string;
    @Input() DocumentData: Documents;
    @Input() LinkEntityId: string;
    @Input() linkEntityType: LinkEntityType;
    @Input() filename: string;
    @Input() content: string;
    @Input() Type: String;
    set attachment(value: Attachments) {
        if (value) {
            this.initialization(value);
            this._attachment = value;
        }
    }
    get attachment(): Attachments {
        return this._attachment;
    }

    documentUploadForm: UntypedFormGroup;
    uploadedFile: any;
    _attachment: Attachments;
    Validators: any;
    LinkEntityType = LinkEntityType;
    tooltip_position = 'above';
    allowChildrenInherit: boolean = true;
    constructor(
        public modalService: NgbActiveModal,
        private formBuilder: UntypedFormBuilder,
        private globalEmittersService: GlobalEmittersService,
        private customToastService: CustomToastService,
        private user_utilty: UserUtilityService,
        private customValidator: CustomValidatorService
    ) {}

    ngOnInit(): void {
        if (this.modalMode === 'Edit' && this.DocumentData) {
            this.allowChildrenInherit = this.DocumentData.allowChildrenInherit;
        }
        this.initialization(this._attachment);
    }

    getMimeType(fileName: string): string {
        const extension = fileName.split('.').pop()?.toLowerCase();

        const mimeTypes: { [key: string]: string } = {
            jpg: 'image/jpg',
            jpeg: 'image/jpeg',
            png: 'image/png',
            pdf: 'application/pdf',
            xls: 'application/vnd.ms-excel',
            xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        };

        return mimeTypes[extension || ''] || 'application/octet-stream';
    }

    convertBase64ToFile(base64String: string, fileName: string): File {
        const byteCharacters = atob(base64String);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray]);

        return new File([blob], fileName, { type: this.getMimeType(fileName) });
    }

    initialization(attachment?: any) {
        if (this.DocumentData) {
            this.documentUploadForm = this.formBuilder.group({
                documentTitle: new FormControl(this.DocumentData.name, [Validators.required, Validators.maxLength(255)]),
                documentNo: new FormControl(this.DocumentData.number, [Validators.required, Validators.maxLength(255)]),
                documentRevision: new FormControl(this.DocumentData.revision, [Validators.required, Validators.maxLength(255)]),
                documentComments: new FormControl(this.DocumentData?.comments ?? '', []),
                AllowChildrenInherit: new FormControl(this.DocumentData.allowChildrenInherit ?? true),
                documentUpload: new FormControl(false),
            });
            if (this.DocumentData?.content) {
                this.uploadedFile = this.convertBase64ToFile(this.DocumentData?.content, this.DocumentData?.filename);
            }
        } else if (attachment) {
            this.documentUploadForm = this.formBuilder.group({
                documentTitle: new FormControl(attachment?.name, [Validators.required, , Validators.maxLength(255)]),
                documentNo: new FormControl(attachment?.number, [Validators.required, Validators.maxLength(255)]),
                documentRevision: new FormControl(attachment?.revision, [Validators.required, Validators.maxLength(255)]),
                documentComments: new FormControl(attachment?.comments ?? '', []),
                AllowChildrenInherit: new FormControl(false),
                documentUpload: new FormControl(false),
            });
            if (this.content) {
                this.uploadedFile = this.convertBase64ToFile(this.content, this.filename);
            }
        } else {
            this.documentUploadForm = this.formBuilder.group({
                documentTitle: new FormControl('', [Validators.required, Validators.maxLength(255)]),
                documentNo: new FormControl('', [Validators.required, Validators.maxLength(255)]),
                documentRevision: new FormControl('', [Validators.required, Validators.maxLength(255)]),
                documentComments: new FormControl('', []),
                AllowChildrenInherit: new FormControl(true),
                documentUpload: new FormControl(null),
            });
        }

        this.Validators = [
            {
                key: 'required',
                value: 'true',
                message: 'This field is required!',
            },
        ];
    }

    fileChangeEvent(event: any) {
        let file = <File>event.target.files[0];

        if (file) {
            let allowedTypes = [
                'image/jpeg',
                'image/jpg',
                'image/png',
                'image/webp',
                'application/pdf',
                'application/vnd.ms-excel', // .xls
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
            ];
            let errorMessage = 'Please select a .jpeg, .jpg, .png, .webp, .pdf, .xls, or .xlsx file.';
            if (this.Type === 'Document' && file.type != 'application/pdf') {
                allowedTypes = ['application/pdf'];
                errorMessage = 'Selected file is not a PDF.';
            }
            if (!allowedTypes.includes(file.type)) {
                this.customToastService.errorToast(errorMessage, 'Error');
                return;
            }
            this.uploadedFile = file;
            this.documentUploadForm.get('documentUpload').setValue(true);
            this.documentUploadForm.markAsDirty();
        }
    }

    btnAddClicked() {
        const formData = new FormData();
        const allowedTypes = [
            'application/pdf',
            'image/png',
            'image/jpeg',
            'image/jpg',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ];
        if (!this.uploadedFile && this.Type != 'Document') {
            this.user_utilty.errorModelPopup('Please select a file!!');
            return;
        }
        if (this.uploadedFile && !allowedTypes.includes(this.uploadedFile.type) && this.Type != 'Document') {
            this.user_utilty.errorModelPopup('Selected files are not supported.  Please check the allowed file types and try again');
            return;
        }
        if (this.uploadedFile && this.Type === 'Document' && this.uploadedFile.type != 'application/pdf') {
            this.user_utilty.errorModelPopup('Selected file is not a PDF.');
            return;
        }
        if (this.modalMode === 'Add') {
            const addAttachment = {
                Name: this.documentUploadForm.get('documentTitle').value,
                Number: this.documentUploadForm.get('documentNo').value,
                Revision: this.documentUploadForm.get('documentRevision').value,
                Comments: this.documentUploadForm.get('documentComments').value,
                file: this.uploadedFile,
            };
            if ((this.linkEntityType as LinkEntityType) === LinkEntityType.TaggedItem && this.Type === 'Document') {
                for (const [key, value] of Object.entries(addAttachment)) {
                    formData.append(key === 'file' ? key : `document[${key}]`, value);
                }
                formData.append(`document[AllowChildrenInherit]`, this.documentUploadForm.get('AllowChildrenInherit').value);
                if (this.LinkEntityId) {
                    formData.append(`document[LinkEntityType]`, LinkEntityType.TaggedItem);
                    formData.append(`document[LinkEntityId]`, this.LinkEntityId);
                }
            } else {
                for (const [key, value] of Object.entries(addAttachment)) {
                    formData.append(key === 'file' ? key : `attachment[${key}]`, value);
                }
                if (this.LinkEntityId) {
                    formData.append(`attachment[LinkEntityType]`, this.linkEntityType);
                    formData.append(`attachment[LinkEntityId]`, this.LinkEntityId);
                }
            }
        }

        if (this.modalMode === 'Edit') {
            if ((this.linkEntityType as LinkEntityType) === LinkEntityType.TaggedItem && this.Type === 'Document') {
                formData.append(`document[documentId]`, this.DocumentData.documentId);
                formData.append(`document[Name]`, this.documentUploadForm.get('documentTitle').value);
                formData.append(`document[Number]`, this.documentUploadForm.get('documentNo').value);
                formData.append(`document[Revision]`, this.documentUploadForm.get('documentRevision').value);
                formData.append(`document[Comments]`, this.documentUploadForm.get('documentComments').value);
                formData.append(`document[AllowChildrenInherit]`, this.documentUploadForm.get('AllowChildrenInherit').value);
                if (this.LinkEntityId) {
                    formData.append(`document[LinkEntityType]`, this.linkEntityType);
                    formData.append(`document[LinkEntityId]`, this.LinkEntityId);
                }
                if (this.documentUploadForm.get('documentUpload').value) {
                    formData.append(`file`, this.uploadedFile);
                } else formData.append(`file`, null);
            } else {
                formData.append(`attachment[documentId]`, this._attachment.documentId);
                formData.append(`attachment[Name]`, this.documentUploadForm.get('documentTitle').value);
                formData.append(`attachment[Number]`, this.documentUploadForm.get('documentNo').value);
                formData.append(`attachment[Revision]`, this.documentUploadForm.get('documentRevision').value);
                formData.append(`attachment[Comments]`, this.documentUploadForm.get('documentComments').value);
                if (this.LinkEntityId) {
                    formData.append(`attachment[LinkEntityType]`, this.linkEntityType);
                    formData.append(`attachment[LinkEntityId]`, this.LinkEntityId);
                }
                if (this.documentUploadForm.get('documentUpload').value) {
                    formData.append(`file`, this.uploadedFile);
                }
            }
        }
        if ((this.linkEntityType as LinkEntityType) === LinkEntityType.TaggedItem && this.Type === 'Document') {
            const Data = {
                Type: this.modalMode,
                FormData: formData,
            };
            this.globalEmittersService.documentFormData.next(Data);
        } else {
            this.modalService.close(formData);
            this.documentUploadForm.reset();
        }
    }

    removeUploadedFile() {
        this.uploadedFile = null;
        this.documentUploadForm.get('documentUpload').setValue(false);
        this.documentUploadForm.markAsDirty();
    }

    checkControl(controlname) {
        return this.documentUploadForm.get(`${controlname}`);
    }

    updateAllowChildrenInherit(event: any) {
        this.allowChildrenInherit = event.checked;
    }

    consoleForm() {}

    onKeyPress(event: KeyboardEvent) {
        this.customValidator.validateKeyPress(event);
    }
}
